import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { FormError } from "types/topicflow";

import { validateProgramForm } from "./form-data";
import { ProgramForm, ProgramFormTab, programFormTabOrder } from "./types";

export const fieldToTab: Record<string, ProgramFormTab> = {
  title: ProgramFormTab.Workflow,
  startDate: ProgramFormTab.Workflow,
  dueDate: ProgramFormTab.Workflow,
  startDateOffset: ProgramFormTab.Workflow,
  startDateDueDateOffset: ProgramFormTab.Workflow,
  startDatePeriodStartOffset: ProgramFormTab.Workflow,
  startDatePeriodEndOffset: ProgramFormTab.Workflow,
  periodStartDate: ProgramFormTab.Workflow,
  periodEndDate: ProgramFormTab.Workflow,
  questionSetIds: ProgramFormTab.Questions,
  topicTemplateId: ProgramFormTab.Workflow,
  appliesTo: ProgramFormTab.Participants,
};

const lastStep = programFormTabOrder[programFormTabOrder.length - 1];

/** The tab is kept in the address (`?step=questions`) so a refresh stays on it
 * and the browser's back button goes to the previous step. */
const stepParam = "step";
const stepSlugs: Record<ProgramFormTab, string> = {
  [ProgramFormTab.Workflow]: "workflow",
  [ProgramFormTab.Questions]: "questions",
  [ProgramFormTab.Participants]: "participants",
  [ProgramFormTab.Notifications]: "notifications",
  [ProgramFormTab.Readiness]: "readiness",
};
const stepFromSlug = (slug: string | null) =>
  programFormTabOrder.find((step) => stepSlugs[step] === slug);

export const getTabsWithErrors = (errors: FormError[]) =>
  new Set(
    errors
      .map((error) => fieldToTab[error.field])
      .filter((tab): tab is ProgramFormTab => tab !== undefined),
  );

/** Who takes part. Readiness's figures are about these people, so changing
 * them means Readiness has to be looked at again before publishing. */
export const getParticipantsKey = (program: ProgramForm) =>
  JSON.stringify([
    program.appliesTo,
    program.appliesToTeams.map(({ id }) => id),
    program.appliesToManagers.map(({ id }) => id),
    program.appliesToUsers.map(({ id }) => id),
    program.excludedUsers.map(({ id }) => id),
    program.excludedHireDateAfter ?? null,
  ]);

/** How far a saved draft had got: every step up to the first one with an
 * error is done, and that one is open. Readiness is opened but never done,
 * because it only counts once it has been seen. */
export const getRestoredSteps = (program: ProgramForm) => {
  const tabsWithErrors = getTabsWithErrors(
    validateProgramForm(program, "publish"),
  );
  const reached = new Set<ProgramFormTab>();
  const completed = new Set<ProgramFormTab>();
  for (const step of programFormTabOrder) {
    reached.add(step);
    if (step === lastStep || tabsWithErrors.has(step)) {
      break;
    }
    completed.add(step);
  }
  return { reached, completed };
};

/** A draft is set up one step at a time: tabs it has not reached are locked,
 * and it can only be published once every step is done. A published review
 * (`isGuidedFlow` false) keeps free tabs. */
const useProgramFormSteps = ({
  program,
  errors,
  isGuidedFlow,
}: {
  program: ProgramForm;
  errors: FormError[];
  isGuidedFlow: boolean;
}) => {
  const history = useHistory();
  const location = useLocation();
  const [reachedSteps, setReachedSteps] = useState(
    () => new Set([ProgramFormTab.Workflow]),
  );
  const [completedSteps, setCompletedSteps] = useState(
    () => new Set<ProgramFormTab>(),
  );
  // The participants Readiness was last seen for.
  const [readinessSeenFor, setReadinessSeenFor] = useState<string | null>(null);

  const isTabLocked = (tab: ProgramFormTab) =>
    isGuidedFlow && !reachedSteps.has(tab);

  // An address naming a step not reached yet, say a pasted link, falls back
  // to the first step.
  const stepInUrl = stepFromSlug(
    new URLSearchParams(location.search).get(stepParam),
  );
  const selectedTab =
    stepInUrl !== undefined && !isTabLocked(stepInUrl)
      ? stepInUrl
      : ProgramFormTab.Workflow;

  const participantsKey = getParticipantsKey(program);
  useEffect(() => {
    if (selectedTab === lastStep) {
      setReadinessSeenFor(participantsKey);
    }
  }, [selectedTab, participantsKey]);

  const selectTab = useCallback(
    (tab: ProgramFormTab) => {
      setReachedSteps((prev) =>
        prev.has(tab) ? prev : new Set(prev).add(tab),
      );
      const params = new URLSearchParams(location.search);
      if (params.get(stepParam) === stepSlugs[tab]) {
        return;
      }
      params.set(stepParam, stepSlugs[tab]);
      history.push({ pathname: location.pathname, search: params.toString() });
    },
    [history, location.pathname, location.search],
  );

  const completeStep = useCallback((step: ProgramFormTab) => {
    setCompletedSteps((prev) => new Set(prev).add(step));
  }, []);

  const restoreSteps = useCallback((savedProgram: ProgramForm) => {
    const { reached, completed } = getRestoredSteps(savedProgram);
    setReachedSteps(reached);
    setCompletedSteps(completed);
  }, []);

  const tabsWithErrors = getTabsWithErrors(errors);
  const isReadinessSeen = readinessSeenFor === participantsKey;
  // A step that was done and has since gained an error no longer counts.
  const doneSteps = new Set(
    programFormTabOrder.filter((step) =>
      step === lastStep
        ? isReadinessSeen
        : completedSteps.has(step) && !tabsWithErrors.has(step),
    ),
  );
  // Every step but Review & publish, which is where the steps end.
  const stepsBeforeEndDone = programFormTabOrder
    .slice(0, -1)
    .every((step) => doneSteps.has(step));
  const allStepsDone = stepsBeforeEndDone && doneSteps.has(lastStep);
  const publishBlockedReason =
    !isGuidedFlow || allStepsDone
      ? null
      : readinessSeenFor !== null && !isReadinessSeen && stepsBeforeEndDone
        ? "Who takes part has changed. Look over Review & publish again to publish."
        : `Finish steps 1–${programFormTabOrder.length - 1}, then look over Review & publish`;

  return {
    selectedTab,
    selectTab,
    isTabLocked,
    doneSteps,
    stepsBeforeEndDone,
    completeStep,
    restoreSteps,
    publishBlockedReason,
  };
};

export default useProgramFormSteps;
